/** @jsxImportSource @emotion/react */
import {
  Checkbox as MuiCheckbox,
  Paper as MuiPaper,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Typography as MuiTypography,
} from '@material-ui/core';
import { Buttons, Loading, Menu } from '@zip/business-components';
import * as Icons from '@zip/react-icons/fearless';
import { Tooltip } from 'components';
import * as styles from './CheckboxTable.styles';
import { CheckboxTableProps } from './CheckboxTableProps';

const CheckboxTable = ({
  id = 'checkbox-table',
  columns,
  selectedCount = 0,
  rowsInPageCount = 0,
  totalRowCount = 0,
  pageSize = 10,
  bulkActions,
  onSelectAllClick,
  onSelectAllInAccountClick,
  isSelectAllInAccount,
  isLoading = false,
  enableBulkAction = false,
  children,
  ...props
}: CheckboxTableProps): JSX.Element => {
  // Avoid a layout jump when reaching any page with number of entries less than pageSize
  const emptyRows = pageSize - rowsInPageCount;

  return (
    <>
      <MuiTableContainer
        id={`${id}-container`}
        component={MuiPaper}
        elevation={0}
        {...props}
      >
        <MuiTable id={id} stickyHeader={true} css={styles.table}>
          <MuiTableHead id={`${id}-thead`} css={styles.tableHead}>
            {/* Checkbox and Header row */}
            <MuiTableRow id={`${id}-toolbar-actions`}>
              {enableBulkAction && (
                <MuiTableCell css={styles.checkbox}>
                  <MuiCheckbox
                    color="primary"
                    css={styles.selectAllCheckbox}
                    indeterminate={
                      selectedCount > 0 && selectedCount < rowsInPageCount
                    }
                    checked={
                      rowsInPageCount > 0 && selectedCount === rowsInPageCount
                    }
                    onChange={(event): void => onSelectAllClick(event)}
                    tabIndex={-1}
                    disabled={isLoading || rowsInPageCount === 0}
                  />
                </MuiTableCell>
              )}
              {!isLoading && selectedCount > 0 ? (
                <MuiTableCell
                  colSpan={columns.length}
                  css={styles.selectAllToolbar}
                >
                  <Menu
                    id="select-all-toolbar-menu"
                    label="Actions"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    options={bulkActions}
                    color="primary"
                    dense
                  />
                </MuiTableCell>
              ) : (
                columns.map((column) => (
                  <MuiTableCell
                    css={styles.wrapText(!!column.tooltipContent)}
                    key={column.name}
                  >
                    <div css={styles.tableHeader}>
                      {column.name}
                      {column.tooltipContent && (
                        <Tooltip
                          id={`${column.name}-tooltip`}
                          content={column.tooltipContent}
                          placement="bottom"
                          maxWidth={250}
                          arrow
                          interactive
                          dense
                          css={styles.tooltip}
                        >
                          <Icons.InformationCircle />
                        </Tooltip>
                      )}
                    </div>
                  </MuiTableCell>
                ))
              )}
            </MuiTableRow>

            {/* Selection toolbar */}
            {!isLoading && rowsInPageCount > 0 && selectedCount > 1 && (
              <MuiTableRow
                id={`${id}-toolbar-selection`}
                css={styles.selectAllToolbar}
              >
                {/* Ghost checkbox to align text */}
                <MuiTableCell css={styles.checkbox} />

                {selectedCount > 1 && (
                  <MuiTableCell colSpan={columns.length}>
                    <MuiTypography variant="body1">
                      {selectedCount === rowsInPageCount ? 'All ' : ''}
                      <strong>
                        {isSelectAllInAccount ? totalRowCount : selectedCount}
                      </strong>{' '}
                      users on this {isSelectAllInAccount ? 'account' : 'page'}{' '}
                      are selected
                    </MuiTypography>
                    {rowsInPageCount !== totalRowCount && (
                      <Buttons.Text
                        css={styles.underlinedLinks(true)}
                        onClick={(): void =>
                          onSelectAllInAccountClick(!isSelectAllInAccount)
                        }
                      >
                        {isSelectAllInAccount
                          ? 'Clear selection'
                          : `Select all ${totalRowCount} users in this account`}
                      </Buttons.Text>
                    )}
                  </MuiTableCell>
                )}
              </MuiTableRow>
            )}
          </MuiTableHead>

          <MuiTableBody id={`${id}-tbody`} css={styles.tableBody}>
            {isLoading && (
              <MuiTableRow>
                <MuiTableCell
                  // When Bulk Action is enabled, +1 is to cater for checkbox being shown
                  colSpan={
                    enableBulkAction ? columns?.length + 1 : columns?.length
                  }
                  css={styles.loading}
                >
                  <Loading width={80} height={30} />
                </MuiTableCell>
              </MuiTableRow>
            )}
            {!isLoading && (
              <>
                {children}
                {totalRowCount > pageSize && emptyRows > 0 && (
                  <MuiTableRow css={styles.emptyRows(emptyRows)}>
                    <MuiTableCell
                      colSpan={
                        // When Bulk Action is enabled, +1 is to cater for checkbox being shown
                        enableBulkAction ? columns?.length + 1 : columns?.length
                      }
                    />
                  </MuiTableRow>
                )}
              </>
            )}
          </MuiTableBody>
        </MuiTable>
      </MuiTableContainer>
      {/* END TABLE */}
    </>
  );
};

export default CheckboxTable;
